import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import {
  List,
  Tooltip,
  ListItem,
  Typography,
  IconButton,
  ListItemText,
  InputAdornment,
} from '@mui/material';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';

import { useChannelContext } from '../hooks';
import { useConnectWhatsApp } from '../hooks/useConnectWhatsApp';

export function Step2() {
  const {
    onChangeStep,
    state: channelState,
    handleChannelName,
    handleWhatsappPin,
    handleFaceBookDeteail,
  } = useChannelContext();

  const { t } = useTranslate('translation');
  const showPassword = useBoolean();

  // ✅ Define schema
  const CampaignSchema = z.object({
    channelName: z.string().min(1, { message: t('CHANNEL_NAME_REQUIRED') }),
    pin: z
      .string()
      .min(6, { message: t('PIN_MUST_BE_6_DIGITS') })
      .max(6, { message: t('PIN_MUST_BE_6_DIGITS') })
      .regex(/^\d{6}$/, { message: t('PIN_NUMERIC_ONLY') }),
    pinAcknowledge: z.boolean().refine((val) => val === true, {
      message: t('PLEASE_ACKNOWLEDGE_WARNINGS'),
    }),
  });

  type CampaignField = z.infer<typeof CampaignSchema>;

  const defaultValues: CampaignField = {
    channelName: '',
    pin: '',
    pinAcknowledge: false,
  };

  const methods = useForm<CampaignField>({
    mode: 'all',
    resolver: zodResolver(CampaignSchema),
    defaultValues,
    values: {
      channelName: channelState.channelName,
      pin: channelState.whatsappPin,
      pinAcknowledge: false,
    },
  });

  const { reset, handleSubmit, watch, formState } = methods;

  const channelName = watch('channelName');
  const isFormValid = formState.isValid;

  const onSubmit = handleSubmit(async (data) => {
    handleChannelName(data.channelName);
    handleWhatsappPin(data?.pin);
    reset();
  });

  const { connect, isWhatsappConnectSuccess } = useConnectWhatsApp({
    onSuccess: handleFaceBookDeteail,
  });

  return (
    <Form methods={methods} onSubmit={onSubmit} style={{ height: '100%' }}>
      <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Grid container spacing={3} justifyContent="space-between">
            <Field.Text
              name="channelName"
              label={t('CHANNEL_NAME')}
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder={t('CHANNEL_NAME')}
            />

            <Box sx={{ my: 2, width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                sx={{ background: '#50a28a', my: 2 }}
                onClick={connect}
                disabled={!channelName}
              >
                {t('SIGN_IN_WITH')} {t('FACEBOOK')}
              </Button>

              {isWhatsappConnectSuccess && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                  <Iconify icon="eva:checkmark-fill" sx={{ color: 'primary.main' }} />
                  <Typography sx={{ color: '#50a28a' }}>{t('ACCOUNT_VERIFIED')}</Typography>
                </Box>
              )}
            </Box>
            {isWhatsappConnectSuccess && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Field.Text
                  name="pin"
                  label={t('2FA_PIN')}
                  type={showPassword.value ? 'text' : 'password'}
                  placeholder={t('SET_6_DIGIT_PIN')}
                  slotProps={{
                    input: {
                      inputProps: {
                        pattern: '[0-9]*',
                        inputMode: 'numeric',
                        maxLength: 6,
                        autoComplete: 'new-password',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={showPassword.value ? t('HIDE') : t('SHOW')}>
                            <IconButton onClick={showPassword.onToggle} edge="end">
                              <Iconify
                                icon={
                                  showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'
                                }
                              />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                    inputLabel: { shrink: true },
                  }}
                  helperText={t('PIN_HELP_TEXT')}
                />

                <Box sx={{ color: 'error.main', fontSize: '0.85rem' }}>
                  <List dense sx={{ pl: 1.5, m: 0 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <ListItem key={i} sx={{ alignItems: 'flex-start', pl: 0 }}>
                        <Iconify
                          icon="fluent-warning-48-filled"
                          sx={{ color: 'error.main', mr: 0.5 }}
                        />
                        <ListItemText
                          primary={t(`PIN_WARNING_${i}`)}
                          primaryTypographyProps={{ fontSize: '0.85rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Field.Checkbox name="pinAcknowledge" label={t('PIN_ACKNOWLEDGEMENT')} />
              </Box>
            )}
          </Grid>
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Button variant="outlined" color="inherit" onClick={() => onChangeStep('back')}>
            {t('PREVIOUS')}
          </Button>

          <Button
            disabled={!isFormValid || !isWhatsappConnectSuccess}
            // disabled={!isFormValid}
            size="small"
            variant="contained"
            type="submit"
            onClick={() => onChangeStep('next')}
          >
            {t('NEXT')}
          </Button>
        </Box>
      </Box>
    </Form>
  );
}
